import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useAppDarkMode from "../../hooks/useAppDarkMode";

const THEME_VARS = {
	light: {
		"--app-body-bg": "#fafafa",
		"--app-surface": "#ffffff",
		"--app-border": "#e4e4e7",
		"--app-text": "#18181b",
		"--app-text-muted": "#71717a",
	},
	dark: {
		"--app-body-bg": "#09090b",
		"--app-surface": "#18181b",
		"--app-border": "#3f3f46",
		"--app-text": "#fafafa",
		"--app-text-muted": "#a1a1aa",
	},
};

/**
 * Modal isolado: estado local evita re-render da página inteira a cada tecla.
 */
export default function DocEditorModal({
	mode,
	initialForm,
	editing,
	users,
	categories = [],
	saving,
	uploading,
	onClose,
	onSave,
	onUploadImage,
	onAddImageUrl,
	onRemoveAttachment,
}) {
	const darkMode = useAppDarkMode();
	const themeKey = darkMode ? "dark" : "light";
	const [form, setForm] = useState(initialForm);
	const [pendingFile, setPendingFile] = useState(null);
	const [pendingPreview, setPendingPreview] = useState("");
	const fileRef = useRef(null);
	const scrollRef = useRef(null);

	useEffect(() => {
		setForm(initialForm);
		setPendingFile(null);
		setPendingPreview((prev) => {
			if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
			return "";
		});
	}, [initialForm]);

	const clearPending = () => {
		setPendingFile(null);
		setPendingPreview((prev) => {
			if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
			return "";
		});
		if (fileRef.current) fileRef.current.value = "";
	};

	const handleClose = () => {
		clearPending();
		onClose();
	};

	const handlePick = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (!file.type.startsWith("image/")) return;
		setPendingFile(file);
		setPendingPreview((prev) => {
			if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
			return URL.createObjectURL(file);
		});
	};

	const node = (
		<div
			className="company-docs-modal-root tailwick-app fixed inset-0 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4"
			data-theme={themeKey}
			style={{
				...THEME_VARS[themeKey],
				background: darkMode ? "rgba(0, 0, 0, 0.72)" : "rgba(24, 24, 27, 0.45)",
				color: "var(--app-text)",
			}}
			onClick={handleClose}
			onWheel={(e) => e.preventDefault()}
			onTouchMove={(e) => {
				if (!scrollRef.current?.contains(e.target)) e.preventDefault();
			}}
			role="presentation"
		>
			<div
				className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[min(90vh,720px)] bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] shadow-xl"
				style={{ contain: "layout paint" }}
				onClick={(e) => e.stopPropagation()}
				onWheel={(e) => e.stopPropagation()}
				role="dialog"
			>
				<div className="flex items-center justify-between px-6 py-4 border-b border-[var(--app-border)] shrink-0">
					<h3 className="text-lg font-semibold m-0 text-[var(--app-text)]">
						{mode === "new" ? "Novo informativo" : "Editar informativo"}
					</h3>
					<button
						type="button"
						onClick={handleClose}
						className="w-8 h-8 flex items-center justify-center rounded-lg border-0 bg-transparent cursor-pointer text-[var(--app-text-muted)]"
					>
						✕
					</button>
				</div>

				<div
					ref={scrollRef}
					className="p-6 flex flex-col gap-4 overflow-y-auto min-h-0 flex-1 bg-[var(--app-surface)]"
					style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
				>
					<div>
						<label className="text-xs font-medium mb-1 block text-[var(--app-text-muted)]">
							Título *
						</label>
						<input
							value={form.title}
							onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
							placeholder="Título do informativo"
							className="w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
						/>
					</div>
					<div>
						<label className="text-xs font-medium mb-1 block text-[var(--app-text-muted)]">
							Categoria
						</label>
						<select
							value={form.category}
							onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
							className="w-full px-3 py-2.5 rounded-xl text-sm outline-none appearance-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
						>
							{categories.length === 0 ? (
								<option value="">Crie uma categoria primeiro</option>
							) : (
								categories.map((c) => (
									<option key={c.slug} value={c.slug}>
										{c.name}
									</option>
								))
							)}
						</select>
					</div>
					<div>
						<label className="text-xs font-medium mb-1 block text-[var(--app-text-muted)]">
							Data (opcional)
						</label>
						<input
							type="date"
							value={form.eventDate}
							onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))}
							className="w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
						/>
					</div>
					<div>
						<label className="text-xs font-medium mb-1 block text-[var(--app-text-muted)]">
							Conteúdo
						</label>
						<textarea
							value={form.body}
							onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
							placeholder="Descreva o informativo..."
							rows={5}
							className="w-full px-3 py-2.5 rounded-xl text-sm outline-none resize-y min-h-[8rem] bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
						/>
					</div>
					<label className="flex items-center gap-2 cursor-pointer text-[var(--app-text)]">
						<input
							type="checkbox"
							checked={form.isPinned}
							onChange={(e) => setForm((f) => ({ ...f, isPinned: e.target.checked }))}
							className="w-4 h-4 rounded accent-indigo-500"
						/>
						<span className="text-sm">Fixar informativo</span>
					</label>

					<div>
						<label className="text-xs font-medium mb-1 block text-[var(--app-text-muted)]">
							Imagem
						</label>
						{editing?.id ? (
							<div className="flex flex-col gap-3">
								{(editing.attachments || []).map((att) => (
									<div
										key={att.id}
										className="flex items-center gap-2 rounded-xl border border-[var(--app-border)] bg-[var(--app-body-bg)] p-2"
									>
										{att.type === "image" ? (
											<img
												src={att.url}
												alt={att.caption || "anexo"}
												className="h-12 w-12 rounded-lg object-cover shrink-0"
											/>
										) : null}
										<a
											href={att.url}
											target="_blank"
											rel="noreferrer"
											className="text-xs text-indigo-500 truncate flex-1"
										>
											{att.caption || att.url}
										</a>
										<button
											type="button"
											onClick={() => onRemoveAttachment(att.id)}
											className="text-xs text-red-500 border-0 bg-transparent cursor-pointer shrink-0"
										>
											Remover
										</button>
									</div>
								))}
								<label className="rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 py-5 border-[var(--app-border)] text-[var(--app-text-muted)]">
									<p className="text-xs m-0">
										{uploading ? "Enviando..." : "Clique para enviar imagem"}
									</p>
									<input
										ref={fileRef}
										type="file"
										accept="image/*"
										className="hidden"
										onChange={onUploadImage}
										disabled={uploading}
									/>
								</label>
								<div className="flex gap-2">
									<input
										value={form.imageUrl || ""}
										onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
										placeholder="Ou cole a URL da imagem"
										className="flex-1 px-3 py-2 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
									/>
									<button
										type="button"
										disabled={uploading || !(form.imageUrl || "").trim()}
										onClick={() => onAddImageUrl(form.imageUrl)}
										className="px-3 py-2 rounded-xl text-sm font-medium text-white bg-indigo-500 border-0 cursor-pointer disabled:opacity-50"
									>
										Vincular
									</button>
								</div>
							</div>
						) : (
							<div className="flex flex-col gap-3">
								<label className="rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 py-5 border-[var(--app-border)] text-[var(--app-text-muted)]">
									{pendingPreview ? (
										<img
											src={pendingPreview}
											alt="preview"
											className="max-h-32 rounded-lg object-cover"
										/>
									) : (
										<p className="text-xs m-0">Clique para adicionar imagem</p>
									)}
									<input
										ref={fileRef}
										type="file"
										accept="image/*"
										className="hidden"
										onChange={handlePick}
									/>
								</label>
								{pendingPreview ? (
									<button
										type="button"
										onClick={clearPending}
										className="text-xs self-start text-red-500 border-0 bg-transparent cursor-pointer"
									>
										Remover imagem
									</button>
								) : null}
								<input
									value={form.imageUrl || ""}
									onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
									placeholder="Ou cole a URL da imagem"
									className="w-full px-3 py-2.5 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
								/>
							</div>
						)}
					</div>

					{users.length > 0 ? (
						<div>
							<p className="text-xs font-medium mb-2 text-[var(--app-text-muted)]">
								Marcar usuários
							</p>
							<div className="flex flex-wrap gap-2 max-h-24 overflow-auto">
								{users.map((u) => (
									<label
										key={u.id}
										className="text-xs flex items-center gap-1 cursor-pointer text-[var(--app-text)]"
									>
										<input
											type="checkbox"
											checked={(form.mentionUserIds || []).includes(u.id)}
											onChange={() =>
												setForm((f) => {
													const ids = f.mentionUserIds || [];
													return {
														...f,
														mentionUserIds: ids.includes(u.id)
															? ids.filter((id) => id !== u.id)
															: [...ids, u.id],
													};
												})
											}
										/>
										{u.name}
									</label>
								))}
							</div>
						</div>
					) : null}
				</div>

				<div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--app-border)] shrink-0 bg-[var(--app-surface)]">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 rounded-xl text-sm font-medium border-0 bg-transparent cursor-pointer text-[var(--app-text-muted)]"
					>
						Cancelar
					</button>
					<button
						type="button"
						disabled={saving}
						onClick={() => onSave(form, pendingFile)}
						className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-500 border-0 cursor-pointer disabled:opacity-60"
					>
						{saving
							? "Salvando..."
							: mode === "new"
								? "Criar informativo"
								: "Salvar alterações"}
					</button>
				</div>
			</div>
		</div>
	);

	return createPortal(node, document.body);
}
